import React from 'react';
import {
  Formik,
  Form as FormikForm,
  Field,
  ErrorMessage,
} from 'formik';
import styled from 'styled-components';
import { object as yupObject } from 'yup';

import type { SharedFormProps, ComponentType } from './form.types';

const FormRender:React.FC<SharedFormProps> = ({
  fields,
  validationErrorComponent: ValidationErrorComponent,
  submitComponent: SubmitComponent,
  preSubmitComponent: PreSubmitComponent = () => <></>,
  fieldsContainerComponent: FieldsContainerComponent,
  submitContainerComponent: SubmitContainerComponent,
  labelComponent: LabelComponent,
  fieldWrapComponent: FieldWrapComponent,
  fieldInnerWrapComponent: FieldInnerWrapComponent,
  onSubmit,
}) => {
  const [focussedFieldSlug, setFocussedFieldSlug] = React.useState('');
  const initialValues = fields.reduce((acc, item) => ({
    ...acc,
    [item.slug]: item.initialValue,
  }), {});

  const validationSchema = fields.reduce((acc, item) => ({
    ...acc,
    [item.slug]: item.validator,
  }), {});


  const customElement = React.useCallback(
    (Component?: ComponentType): ComponentType | undefined => {
      if (typeof Component === 'undefined') return undefined;
      return ({ field, ...props }: any) => (
        <Component {...props} {...field} />
      );
    }, [],
  );

  const getFieldClassName = (slug: string, value: string, type = 'unknown'): string => {
    const focusStateClass = (focussedFieldSlug === slug) || value
      ? 'focussed-or-has-value'
      : 'empty-and-unfocussed';
    return [
      focusStateClass,
      `type-${type}`,
    ].join(' ');
  };

  const SubmitButton = SubmitComponent || DefaultButton;
  const FieldsContainer = FieldsContainerComponent || DefaultFieldsContainer;
  const SubmitContainer = SubmitContainerComponent || DefaultSubmitContainer;
  const ValidationError = ValidationErrorComponent || DefaultValidationErrorComponent;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={yupObject(validationSchema)}
      onSubmit={onSubmit}
      validateOnMount
    >
      {(formInfo) => {
        const {
          isSubmitting,
          isValid,
          values,
          handleBlur,
          handleChange,
        } = formInfo;
        return (
          <FormikForm>
            <FieldsContainer>
              {fields.map((field) => {
                /* eslint-disable max-len */
                const Label = field.labelComponent || LabelComponent || DefaultLabel;
                const FieldWrap = field.wrapComponent || FieldWrapComponent || DefaultFieldWrap;
                const FieldInnerWrap = field.innerWrapComponent || FieldInnerWrapComponent || DefaultFieldInnerWrap;
                /* eslint-enable max-len */
                // theh following prevents dom error when using onChange with file input
                const conditionalValueUndefinedProp = field.inputType === 'file' ? { value: undefined } : {};
                return (
                  <FieldWrap key={field.slug}>
                    <FieldInnerWrap>
                      <Label
                        className={getFieldClassName(
                          field.slug,
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                          (values as any)[field.slug],
                          field.inputType || (typeof field.as === 'string' ? field.as : ''),
                        )}
                        htmlFor={field.slug}
                      >
                        {field.label}
                      </Label>
                      <Field
                        name={field.slug}
                        type={field.inputType}
                        placeholder={field.placeholder}
                        as={field.as}
                        component={customElement(field.component)}
                        onFocus={() => setFocussedFieldSlug(field.slug)}
                        onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleBlur(e);
                          setFocussedFieldSlug('');
                        }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const handler = field.onChange || handleChange;
                          handler(e, formInfo);
                        }}
                        {...conditionalValueUndefinedProp}
                      />
                    </FieldInnerWrap>
                    <ErrorMessage
                      name={field.slug}
                      component={customElement(ValidationError)}
                    />
                  </FieldWrap>
                );
              })}
            </FieldsContainer>
            <PreSubmitComponent />
            <SubmitContainer>
              <SubmitButton
                type="submit"
                disabled={!!(isSubmitting || !isValid)}
              >
                Submit
              </SubmitButton>
            </SubmitContainer>
          </FormikForm>
        );
      }}
    </Formik>
  );
};
export default FormRender;

const DefaultFieldsContainer = styled.div``;
const DefaultFieldWrap = styled.div``;
const DefaultFieldInnerWrap = styled.div``;
const DefaultLabel = styled.label``;
const DefaultSubmitContainer = styled.div``;
const DefaultButton = styled.button``;
const DefaultValidationErrorComponent = styled.div`
  color: red;
`;
